import {
  ArrowRight,
  BriefcaseBusiness,
  CalendarCheck,
  CheckCircle2,
  Clock3,
  FileText,
  FolderGit2,
  GraduationCap,
  LineChart,
  Sparkles,
  Trophy,
} from 'lucide-react'
import { Component, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { completeStudentDailyTask, getGrowthSummary } from '../../api.js'
import { useAuth } from '../../auth-context.js'
import { useLanguage } from '../../contexts/LanguageContext.jsx'
import { hasEffectiveGrowthData } from '../../student-growth-state.js'

function cleanText(value, fallback = '') {
  if (typeof value === 'string') {
    const text = value.trim()
    return text && text !== '[object Object]' ? text : fallback
  }
  if (value && typeof value === 'object') {
    return cleanText(value.title || value.name || value.task || value.description, fallback)
  }
  return fallback
}

function normalizeTask(task, t) {
  return {
    id: String(task?.id || ''),
    title: cleanText(task?.title, t('growthDaily.taskFallback')),
    description: cleanText(task?.description),
    status: task?.status || 'pending',
    estimatedMinutes: Number(task?.estimatedMinutes || 0),
    xpReward: Number(task?.xpReward || 0),
  }
}

function normalizeOutcome(item, type, t) {
  if (type === 'resume') {
    return {
      id: `resume-${item?.id || item?.createdAt || Math.random()}`,
      type,
      title: cleanText(item?.fileName, t('growthDaily.resumeOutcome')),
      description: item?.resumeScore ? t('growthDaily.resumeScore', { score: item.resumeScore }) : t('growthDaily.resumeOutcomeDesc'),
      to: '/more/resume',
    }
  }
  return {
    id: `project-${item?.id || item?.updatedAt || Math.random()}`,
    type,
    title: cleanText(item?.title, t('growthDaily.projectOutcome')),
    description: cleanText(item?.summary || item?.projectGoal, t('growthDaily.projectOutcomeDesc')),
    to: item?.id ? `/projects/${encodeURIComponent(item.id)}` : '/projects',
  }
}

function normalizeNextPath(path, fallback = '/growth') {
  if (path === '/student-growth/today') return '/today'
  if (path === '/student-projects') return '/projects'
  if (path === '/resume') return '/more/resume'
  if (path === '/student-growth/profile') return '/ai-partner'
  return path || fallback
}

function resolveGrowthNextPath(nextStep, { hasGrowthData, hasTasks }) {
  if (!hasGrowthData) return '/explore'
  const targetPath = nextStep?.targetPath || ''
  if (
    nextStep?.type === 'learning'
    || targetPath === '/student-growth/profile'
    || targetPath === '/ai-partner'
    || targetPath === '/explore'
    || targetPath.startsWith('/growth/explore')
  ) {
    return '/learning'
  }
  return normalizeNextPath(targetPath, hasTasks ? '/today' : '/learning')
}

class GrowthPageErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error) {
    console.error('[growth-page]', error)
  }

  render() {
    if (this.state.hasError) {
      const { t } = this.props
      return (
        <main className="page-container student-growth-center-page">
          <section className="student-growth-daily-assistant">
            <div>
              <span><Sparkles size={18} /> {t('growthDaily.errorEyebrow')}</span>
              <h2>{t('growthDaily.errorTitle')}</h2>
              <p>{t('growthDaily.errorDesc')}</p>
            </div>
            <Link className="primary-button" to="/growth">
              {t('growthDaily.retry')}
            </Link>
            <Link className="secondary-button" to="/">
              {t('common.backHome')}
            </Link>
          </section>
        </main>
      )
    }

    return this.props.children
  }
}

function GrowthLoginState({ t }) {
  const previewItems = [
    { icon: GraduationCap, label: t('growthDaily.previewGoal'), value: t('growthDaily.previewGoalValue') },
    { icon: Clock3, label: t('growthDaily.previewStage'), value: t('growthDaily.previewStageValue') },
    { icon: CalendarCheck, label: t('growthDaily.previewTask'), value: t('growthDaily.previewTaskValue') },
    { icon: Sparkles, label: t('growthDaily.previewNext'), value: t('growthDaily.previewNextValue') },
    { icon: FolderGit2, label: t('growthDaily.previewOutcome'), value: t('growthDaily.previewOutcomeValue') },
  ]

  return (
    <main className="page-container student-growth-center-page growth-daily-space">
      <section className="growth-focus-board growth-focus-board--guest">
        <div className="growth-focus-copy">
          <span><GraduationCap size={18} /> {t('growthDaily.eyebrow')}</span>
          <h1>{t('growthDaily.loginTitle')}</h1>
          <p>{t('growthDaily.loginDesc')}</p>
          <div className="growth-action-row">
            <Link className="primary-button" to="/login">
              {t('common.login')} <ArrowRight size={16} />
            </Link>
            <Link className="secondary-button" to="/explore">
              {t('growthDaily.startDiagnosis')}
            </Link>
          </div>
        </div>
      </section>
      <section className="growth-guest-preview" aria-label={t('growthDaily.previewTitle')}>
        <div>
          <span><Sparkles size={18} /> {t('growthDaily.previewTitle')}</span>
          <p>{t('growthDaily.previewDesc')}</p>
        </div>
        <div className="growth-guest-preview-list">
          {previewItems.map((item) => {
            const Icon = item.icon
            return (
              <article key={item.label}>
                <Icon size={18} />
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </article>
            )
          })}
        </div>
      </section>
    </main>
  )
}

function GrowthTodayWorkspace({ busyTaskId, nextStep, onComplete, outcomes, summary, tasks, t }) {
  const rawCurrentGoal = cleanText(summary?.currentGoal?.title)
  const hasGrowthData = hasEffectiveGrowthData(summary) || tasks.length > 0
  const currentGoal = rawCurrentGoal || (hasGrowthData ? t('growthDaily.existingPlanGoal') : t('growthDaily.noGoal'))
  const currentStage = cleanText(
    summary?.growthStage?.stage,
    hasGrowthData ? t('growthDaily.existingPlanStage') : t('growthDaily.noStage'),
  )
  const progress = Number(summary?.growthStage?.progress || 0)
  const taskSummary = summary?.todayTasks?.summary || {}
  const pending = Number(taskSummary.pending || tasks.filter((task) => task.status !== 'completed').length)
  const focusTask = tasks.find((task) => task.status !== 'completed') || tasks[0]
  const hasGrowthGoal = Boolean(rawCurrentGoal || hasGrowthData)
  const title = hasGrowthData ? cleanText(nextStep?.title, t('growthDaily.nextFallbackTitle')) : t('growthDaily.nextFallbackTitle')
  const description = hasGrowthData ? cleanText(nextStep?.description, t('growthDaily.nextFallbackDesc')) : t('growthDaily.nextFallbackDesc')
  const to = resolveGrowthNextPath(nextStep, { hasGrowthData, hasTasks: tasks.length > 0 })
  const projectTotal = Number(summary?.projectAssets?.total || 0)
  const resumeTotal = Number(summary?.resumeAssets?.total || 0)
  const companionObservation = hasGrowthGoal
    ? pending
      ? t('growthDaily.companionObservationPending', { goal: currentGoal, count: pending })
      : t('growthDaily.companionObservationDone', { goal: currentGoal })
    : t('growthDaily.companionObservationNoGoal')
  const companionAction = focusTask && focusTask.status !== 'completed'
    ? t('growthDaily.companionActionTask', { task: focusTask.title })
    : description

  return (
    <>
      <section className="growth-focus-board">
        <div className="growth-focus-copy">
          <span><Sparkles size={18} /> {t('growthDaily.eyebrow')}</span>
          <h1>{currentGoal}</h1>
          <p>{t('growthDaily.subtitle')}</p>
          <div className="growth-focus-status" aria-label={t('growthDaily.currentState')}>
            <span><GraduationCap size={16} /> {t('growthDaily.currentStage')}: {currentStage}</span>
            <span><Clock3 size={16} /> {t('growthDaily.stageProgress')}: {t('growthDaily.progressValue', { progress })}</span>
          </div>
        </div>

        <aside className="growth-today-panel">
          <div className="growth-panel-heading">
            <span><CalendarCheck size={18} /> {t('growthDaily.todayTasks')}</span>
            <strong>{pending ? t('growthDaily.pendingTasks', { count: pending }) : tasks.length ? t('growthDaily.allDone') : t('growthDaily.noTaskStatus')}</strong>
          </div>
          {focusTask ? (
            <article className={`growth-focus-task ${focusTask.status === 'completed' ? 'is-done' : ''}`}>
              <button
                type="button"
                className="student-growth-task-check"
                disabled={focusTask.status === 'completed' || busyTaskId === focusTask.id}
                onClick={() => onComplete(focusTask)}
                aria-label={focusTask.status === 'completed' ? t('growthDaily.done') : t('growthDaily.markDone')}
              >
                {focusTask.status === 'completed' ? <CheckCircle2 size={20} /> : <span />}
              </button>
              <div>
                <strong>{focusTask.title}</strong>
                {focusTask.description ? <p>{focusTask.description}</p> : null}
                <small>{t('growthDaily.taskMeta', { minutes: focusTask.estimatedMinutes || 0, xp: focusTask.xpReward || 0 })}</small>
              </div>
            </article>
          ) : (
            <article className="growth-focus-task">
              <div>
                <strong>{t('growthDaily.noTaskTitle')}</strong>
                <p>{t('growthDaily.noTaskDesc')}</p>
              </div>
              <Link className="secondary-button" to={hasGrowthData ? '/learning' : '/explore'}>
                {hasGrowthData ? t('growthDaily.viewLearning') : t('growthDaily.findDirection')}
              </Link>
            </article>
          )}
          <Link className="growth-text-link" to={hasGrowthData ? '/today' : '/explore'}>
            {tasks.length ? t('growthDaily.openToday') : t('growthDaily.createFirstTask')} <ArrowRight size={15} />
          </Link>
        </aside>
      </section>

      <section className="growth-next-step">
        <div>
          <span><Sparkles size={18} /> {t('growthDaily.companionAdvice')}</span>
          <h2>{title}</h2>
          <p>{companionObservation}</p>
          <p>{companionAction}</p>
        </div>
        <Link className="primary-button" to={to}>
          {t('growthDaily.doNext')} <ArrowRight size={16} />
        </Link>
      </section>

      <section className="growth-outcome-strip">
        <div className="growth-outcome-summary">
          <span><FolderGit2 size={18} /> {t('growthDaily.recentOutcomes')}</span>
          <strong>{t('growthDaily.outcomeSummary', { projects: projectTotal, resumes: resumeTotal })}</strong>
          <p>{t('growthDaily.outcomeDesc')}</p>
        </div>
        <div className="growth-outcome-list">
          {outcomes.length ? outcomes.slice(0, 2).map((item) => (
            <Link to={item.to} key={item.id}>
              {item.type === 'resume' ? <FileText size={16} /> : <BriefcaseBusiness size={16} />}
              <span>{item.title}</span>
              <small>{item.description}</small>
            </Link>
          )) : (
            <article>
              <FolderGit2 size={16} />
              <span>{t('growthDaily.noOutcomeTitle')}</span>
              <small>{t('growthDaily.noOutcomeDesc')}</small>
            </article>
          )}
        </div>
        <Link className="secondary-button" to="/growth/assets">{t('growthDaily.viewAssets')}</Link>
      </section>
    </>
  )
}

function GrowthSecondaryLinks({ t }) {
  const links = [
    { to: '/growth/report', icon: LineChart, label: t('growthDaily.report'), desc: t('growthDaily.reportDesc') },
    { to: '/growth/timeline', icon: Clock3, label: t('growthDaily.timeline'), desc: t('growthDaily.timelineDesc') },
    { to: '/growth/assets', icon: FolderGit2, label: t('growthDaily.assets'), desc: t('growthDaily.assetsDesc') },
    { to: '/more/competitions', icon: Trophy, label: t('growthDaily.competitions'), desc: t('growthDaily.competitionsDesc') },
    { to: '/more/resume', icon: FileText, label: t('growthDaily.resume'), desc: t('growthDaily.resumeDesc') },
    { to: '/more/interview', icon: BriefcaseBusiness, label: t('growthDaily.interview'), desc: t('growthDaily.interviewDesc') },
  ]
  return (
    <section className="growth-quiet-links" aria-label={t('growthDaily.secondaryActions')}>
      <div>
        <span>{t('growthDaily.secondaryActions')}</span>
      </div>
      <div className="growth-quiet-link-list">
        {links.map((item) => {
          const Icon = item.icon
          return (
            <Link to={item.to} key={item.to} title={item.desc}>
              <Icon size={17} /> {item.label}
            </Link>
          )
        })}
      </div>
    </section>
  )
}

function GrowthDailyPage() {
  const { isLoggedIn } = useAuth()
  const { t } = useLanguage()
  const [state, setState] = useState({ loading: true, error: '', summary: null })
  const [busyTaskId, setBusyTaskId] = useState('')

  useEffect(() => {
    if (!isLoggedIn) {
      setState({ loading: false, error: '', summary: null })
      return undefined
    }
    const controller = new AbortController()
    setState((current) => ({ ...current, loading: true, error: '' }))
    getGrowthSummary(controller.signal)
      .then((summary) => setState({ loading: false, error: '', summary }))
      .catch((error) => {
        if (error.name !== 'AbortError') {
          setState({ loading: false, error: error?.message || t('growthDaily.loadFailed'), summary: null })
        }
      })
    return () => controller.abort()
  }, [isLoggedIn, t])

  const tasks = useMemo(() => (
    Array.isArray(state.summary?.todayTasks?.items)
      ? state.summary.todayTasks.items.map((task) => normalizeTask(task, t))
      : []
  ), [state.summary, t])

  const outcomes = useMemo(() => {
    const projects = Array.isArray(state.summary?.projectAssets?.latest)
      ? state.summary.projectAssets.latest.map((item) => normalizeOutcome(item, 'project', t))
      : []
    const resumes = Array.isArray(state.summary?.resumeAssets?.latest)
      ? state.summary.resumeAssets.latest.map((item) => normalizeOutcome(item, 'resume', t))
      : []
    return [...projects, ...resumes]
  }, [state.summary, t])

  async function completeTask(task) {
    if (!task.id || busyTaskId || task.status === 'completed') return
    setBusyTaskId(task.id)
    try {
      await completeStudentDailyTask(task.id)
      const summary = await getGrowthSummary()
      setState({ loading: false, error: '', summary })
    } catch (error) {
      setState((current) => ({ ...current, error: error?.message || t('growthDaily.completeFailed') }))
    } finally {
      setBusyTaskId('')
    }
  }

  if (!isLoggedIn) return <GrowthLoginState t={t} />

  return (
    <main className="page-container student-growth-center-page growth-daily-space">
      {state.loading ? (
        <section className="growth-focus-board">
          <span><Sparkles size={18} /> {t('growthDaily.loading')}</span>
        </section>
      ) : null}
      {state.error ? (
        <section className="student-growth-error-state">
          <h2>{t('growthDaily.loadFailedTitle')}</h2>
          <p>{state.error}</p>
          <button className="primary-button" type="button" onClick={() => window.location.reload()}>{t('growthDaily.retry')}</button>
        </section>
      ) : null}
      {state.summary ? (
        <>
          <GrowthTodayWorkspace
            busyTaskId={busyTaskId}
            onComplete={completeTask}
            nextStep={state.summary.nextStep}
            outcomes={outcomes}
            summary={state.summary}
            tasks={tasks}
            t={t}
          />
          <GrowthSecondaryLinks t={t} />
        </>
      ) : null}
    </main>
  )
}

function GrowthPage() {
  const { t } = useLanguage()
  return (
    <GrowthPageErrorBoundary t={t}>
      <GrowthDailyPage />
    </GrowthPageErrorBoundary>
  )
}

export default GrowthPage
